import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import styles from "./orthodontics-for-teens.module.css";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { OptimizedImage } from "~/components/OptimizedImage";

type Localization = {
  title: string;
  subtitle: string;
  introFirstParagraph: string;
  introSecondParagraph: string;
  signUpTitle: string;
  signUpContent: string;
  signUpConsultation: string;
  signUpBenefits: string[];
  biteProblemsTitle: string;
  biteProblemsContent: string;
  biteProblemsIntro: string;
  biteProblemsTypes: string[];
  benefitsTitle: string;
  benefitsIntro: string;
  benefitsItems: string[];
  treatmentProcessTitle: string;
  treatmentProcessContent: string;
  treatmentProcessIntro: string;
  metalBracesTitle: string;
  metalBracesContent: string[];
  ceramicBracesTitle: string;
  ceramicBracesContent: string[];
  invisalignTitle: string;
  invisalignContent: string[];
  invisalignNote: string;
};

const englishLocalization: Localization = {
  title: "Orthodontics for Teens",
  subtitle: "Teen Orthodontics",
  introFirstParagraph:
    "Experienced orthodontists like Dr. Garcia-Paul will also be able to easily recognize issues with spacing, crowding, bite, and alignment. In this age range, your teen’s bone, and jaw structure are formed enough to allow orthodontic treatment to begin.",
  introSecondParagraph:
    "Some teens may have even started Phase I treatment at an earlier age, and are now following it up with full braces or other orthodontic treatment.",
  signUpTitle: "Signs Your Teen Needs Treatment",
  signUpContent:
    "Beyond outward appearances, untreated dental problems can have a lasting impact on your teen’s oral and overall health.",
  signUpConsultation:
    "If you notice any of the following issues, we recommend a complimentary consultation with Dr. Garcia-Paul at Miami Orthodontic Specialists. They could be signs your teen may benefit from orthodontic treatment.",
  signUpBenefits: [
    "Early, late, or irregular loss of their baby teeth",
    "Difficulty with chewing or biting",
    "Crowding or extra spacing between teeth",
    "Jaws that shift or make sounds",
    "Frequent biting of the cheek or the inside of the mouth",
    "Teeth that meet abnormally or not at all",
    "Jaws and teeth that are out of proportion to the rest of the face",
    "Protrusion of teeth",
  ],
  biteProblemsTitle: "Bite Problems",
  biteProblemsContent:
    "Diagnosing and successfully treating certain bite problems can have short-term and long-term benefits.",
  biteProblemsIntro:
    "Problems classified as “bad bites” can often be minimized with the proper orthodontic treatment, including:",
  biteProblemsTypes: [
    "Crooked or crowded teeth",
    "Missing or extra teeth",
    "An overbite or underbite",
    "Incorrect jaw position",
    "A disorder of the jaw joint",
  ],
  benefitsTitle: "Benefits of a Straight Smile",
  benefitsIntro:
    "Straight teeth that respond well to orthodontic treatment have many benefits, including:",
  benefitsItems: [
    "Being less prone to wear and tear, chipping, and tooth trauma",
    "Being easier to keep clean, helping to prevent tooth decay and cavities",
    "Reducing the chances of developing gum disease, which can result in bone loss, decay, and tooth loss",
    "Improving proper chewing and digestion of food",
  ],
  treatmentProcessTitle: "The Treatment Process",
  treatmentProcessContent:
    "While creating a customized treatment plan for your teen, we might recommend an orthodontic appliance.",
  treatmentProcessIntro: "Here at Miami Orthodontic Specialists, we offer:",
  metalBracesTitle: "Metal Braces",
  metalBracesContent: [
    "Braces have come a very long way, even within the past decade. When many people think of braces, they think of extremely bulky metal brackets.",
    "We don’t use this type of bulky orthodontic appliance, and many of the new braces are lighter and more comfortable than ever before.",
  ],
  ceramicBracesTitle: "Ceramic Braces",
  ceramicBracesContent: [
    "Also known as clear braces, these work the same way metal braces do but feature brackets made of tooth-colored ceramic materials. They blend in with your teen’s smile, making them much less obvious.",
    "Since ceramic braces are slightly more prone to breakage, and a little costlier than metal braces, they may not be suitable for everyone.",
  ],
  invisalignTitle: "Invisalign® for Teens",
  invisalignContent: [
    "The Invisalign system is made up of a series of fully customized, clear trays called aligners. These aligners are nearly invisible and totally removable.",
    "Made of soft plastic, they provide a more comfortable orthodontic experience, with no friction or abrasions inside your teen’s mouth.",
    "Each week your teen will switch their aligner for the next in the set. When worn the recommended 20-22 hours per day, your teen’s teeth will gradually move into the desired positions.",
  ],
  invisalignNote:
    "Invisalign works best for mild to moderate orthodontic issues, and may not be an option if your teen needs more serious treatment.",
};

const spanishLocalization: Localization = {
  title: "Ortodoncia para adolescentes",
  subtitle: "Ortodoncia para adolescentes",
  introFirstParagraph:
    "Los ortodoncistas experimentados como la Dra. García-Paul también pueden reconocer fácilmente problemas de espacio, apiñamiento, mordida y alineación. En este rango de edad, la estructura ósea y mandibular de tu adolescente está lo suficientemente formada para comenzar el tratamiento de ortodoncia.",
  introSecondParagraph:
    "Algunos adolescentes incluso pueden haber comenzado el tratamiento de Fase I a una edad más temprana, y ahora lo continúan con brackets completos u otro tratamiento de ortodoncia.",
  signUpTitle: "Señales de que tu adolescente necesita tratamiento",
  signUpContent:
    "Más allá de la apariencia, los problemas dentales sin tratar pueden tener un impacto duradero en la salud oral y general de tu adolescente.",
  signUpConsultation:
    "Si notas cualquiera de los siguientes problemas, recomendamos una consulta de cortesía con la Dra. García-Paul en Miami Orthodontic Specialists. Podrían ser señales de que tu adolescente se beneficiaría del tratamiento de ortodoncia.",
  signUpBenefits: [
    "Pérdida temprana, tardía o irregular de sus dientes de leche",
    "Dificultad para masticar o morder",
    "Apiñamiento o espacios adicionales entre los dientes",
    "Mandíbulas que se desplazan o hacen ruidos",
    "Mordida frecuente de la mejilla o del interior de la boca",
    "Dientes que se encuentran de forma anormal o que no se encuentran en absoluto",
    "Mandíbulas y dientes fuera de proporción con el resto de la cara",
    "Protrusión de los dientes",
  ],
  biteProblemsTitle: "Problemas de mordida",
  biteProblemsContent:
    "Diagnosticar y tratar correctamente ciertos problemas de mordida puede tener beneficios a corto y largo plazo.",
  biteProblemsIntro:
    "Los problemas clasificados como “malas mordidas” a menudo pueden minimizarse con el tratamiento de ortodoncia adecuado, incluyendo:",
  biteProblemsTypes: [
    "Dientes torcidos o apiñados",
    "Dientes faltantes o adicionales",
    "Una sobremordida o submordida",
    "Posición incorrecta de la mandíbula",
    "Un trastorno de la articulación mandibular",
  ],
  benefitsTitle: "Beneficios de una sonrisa recta",
  benefitsIntro:
    "Los dientes rectos que responden bien al tratamiento de ortodoncia tienen muchos beneficios, incluyendo:",
  benefitsItems: [
    "Ser menos propensos al desgaste, astillamiento y trauma dental",
    "Ser más fáciles de mantener limpios, ayudando a prevenir caries y cavidades",
    "Reducir las probabilidades de desarrollar enfermedad de las encías, que puede causar pérdida ósea, caries y pérdida dental",
    "Mejorar la masticación y digestión adecuada de los alimentos",
  ],
  treatmentProcessTitle: "El proceso de tratamiento",
  treatmentProcessContent:
    "Al crear un plan de tratamiento personalizado para tu adolescente, podríamos recomendar un aparato de ortodoncia.",
  treatmentProcessIntro: "Aquí en Miami Orthodontic Specialists, ofrecemos:",
  metalBracesTitle: "Brackets metálicos",
  metalBracesContent: [
    "Los brackets han avanzado muchísimo, incluso en la última década. Cuando muchas personas piensan en brackets, imaginan brackets metálicos extremadamente voluminosos.",
    "No usamos ese tipo de aparato de ortodoncia voluminoso, y muchos de los brackets nuevos son más livianos y cómodos que nunca.",
  ],
  ceramicBracesTitle: "Brackets cerámicos",
  ceramicBracesContent: [
    "También conocidos como brackets transparentes, funcionan de la misma manera que los brackets metálicos, pero cuentan con brackets hechos de materiales cerámicos del color del diente. Se mezclan con la sonrisa de tu adolescente, haciéndolos mucho menos visibles.",
    "Como los brackets cerámicos son un poco más propensos a romperse y un poco más costosos que los metálicos, pueden no ser adecuados para todos.",
  ],
  invisalignTitle: "Invisalign® para adolescentes",
  invisalignContent: [
    "El sistema Invisalign está compuesto por una serie de bandejas transparentes totalmente personalizadas llamadas alineadores. Estos alineadores son casi invisibles y completamente removibles.",
    "Hechos de plástico suave, ofrecen una experiencia de ortodoncia más cómoda, sin fricción ni abrasiones dentro de la boca de tu adolescente.",
    "Cada semana tu adolescente cambiará su alineador por el siguiente de la serie. Cuando se usan las 20 a 22 horas recomendadas al día, sus dientes se moverán gradualmente a las posiciones deseadas.",
  ],
  invisalignNote:
    "Invisalign funciona mejor para problemas de ortodoncia leves a moderados, y puede no ser una opción si tu adolescente necesita un tratamiento más complejo.",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export function OrthodonticsForTeensPage() {
  const getLocalization = useLocalization(localized);

  return (
    <>
      <HeroStyle2
        heroImageUrl="/miami-ortho-2.jpg"
        title={getLocalization("title")}
        backgroundPosition="center"
      />

      <Flex
        className={`section ${styles.mainContentSection}`}
        width="100%"
        align="center"
        justify="center"
      >
        <Flex
          direction="column"
          className={styles.contentSection}
          gap="6"
          p="2"
        >
          {/* Introduction Section */}
          <Flex direction="column" gap="3">
            <Heading as="h2" className={styles.heading}>
              {getLocalization("subtitle")}
            </Heading>
            <Text size="5" className={styles.paragraph}>
              {getLocalization("introFirstParagraph")}
            </Text>
            <Text size="5" className={styles.paragraph}>
              {getLocalization("introSecondParagraph")}
            </Text>
          </Flex>

          {/* Signs Your Teen Needs Treatment Section */}
          <Flex direction="column" gap="4">
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("signUpTitle")}
            </Heading>
            <Text size="5" className={styles.paragraph}>
              {getLocalization("signUpContent")}
            </Text>
            <Text size="5" className={styles.paragraph}>
              {getLocalization("signUpConsultation")}
            </Text>
            <Box className={styles.benefitsList}>
              {getLocalization("signUpBenefits").map((benefit) => (
                <Flex key={benefit} gap="2" className={styles.benefitItem}>
                  <Text className={styles.bulletPoint}>•</Text>
                  <Text>{benefit}</Text>
                </Flex>
              ))}
            </Box>
          </Flex>

          {/* Bite Problems Section */}
          <Flex direction="column" gap="4">
            <Heading as="h4" className={styles.subheading}>
              {getLocalization("biteProblemsTitle")}
            </Heading>
            <Text size="5" className={styles.paragraph}>
              {getLocalization("biteProblemsContent")}
            </Text>
            <Text size="5" className={styles.paragraph}>
              {getLocalization("biteProblemsIntro")}
            </Text>
            <Box className={styles.benefitsList}>
              {getLocalization("biteProblemsTypes").map((type) => (
                <Flex key={type} gap="2" className={styles.benefitItem}>
                  <Text className={styles.bulletPoint}>•</Text>
                  <Text>{type}</Text>
                </Flex>
              ))}
            </Box>
          </Flex>

          {/* Benefits Section */}
          <Flex direction="column" gap="4">
            <Heading as="h4" className={styles.subheading}>
              {getLocalization("benefitsTitle")}
            </Heading>
            <Text size="5" className={styles.paragraph}>
              {getLocalization("benefitsIntro")}
            </Text>
            <Box className={styles.benefitsList}>
              {getLocalization("benefitsItems").map((benefit) => (
                <Flex key={benefit} gap="2" className={styles.benefitItem}>
                  <Text className={styles.bulletPoint}>•</Text>
                  <Text>{benefit}</Text>
                </Flex>
              ))}
            </Box>
          </Flex>
        </Flex>
      </Flex>

      {/* Teen Orthodontics Image Section */}
      <Box className={styles.imageSection}>
        <OptimizedImage
          src="/miami-ortho-12-1024x684.jpg"
          alt="Smiling orthodontist with two teenage girls, both wearing braces, outdoors in a tropical setting, promoting Miami orthodontics and the benefits of braces for teens."
          className={styles.contentImage}
        />
      </Box>

      <Flex
        className={`section ${styles.mainContentSection}`}
        width="100%"
        align="center"
        justify="center"
      >
        <Flex
          direction="column"
          className={styles.contentSection}
          gap="8"
          p="2"
        >
          {/* Treatment Process Section */}
          <Flex direction="column" gap="4">
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("treatmentProcessTitle")}
            </Heading>
            <Text size="5" className={styles.paragraph}>
              {getLocalization("treatmentProcessContent")}
            </Text>
            <Text size="5" className={styles.paragraph}>
              {getLocalization("treatmentProcessIntro")}
            </Text>

            {/* Metal Braces */}
            <Flex direction="column" gap="2">
              <Heading as="h4" className={styles.subheading}>
                {getLocalization("metalBracesTitle")}
              </Heading>
              {getLocalization("metalBracesContent").map((paragraph) => (
                <Text key={paragraph} size="5" className={styles.paragraph}>
                  {paragraph}
                </Text>
              ))}
            </Flex>

            {/* Ceramic Braces */}
            <Flex direction="column" gap="2">
              <Heading as="h4" className={styles.subheading}>
                {getLocalization("ceramicBracesTitle")}
              </Heading>
              {getLocalization("ceramicBracesContent").map((paragraph) => (
                <Text key={paragraph} size="5" className={styles.paragraph}>
                  {paragraph}
                </Text>
              ))}
            </Flex>

            {/* Invisalign for Teens Section */}
            <Flex direction="column" gap="2">
              <Heading as="h4" className={styles.subheading}>
                {getLocalization("invisalignTitle")}
              </Heading>
              {getLocalization("invisalignContent").map((paragraph) => (
                <Text key={paragraph} size="5" className={styles.paragraph}>
                  {paragraph}
                </Text>
              ))}
              <Text size="5" className={styles.paragraph}>
                {getLocalization("invisalignNote")}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>

      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
